import { DynDadosCadastraisProps } from ".";
import { FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { validarCpfCnpj } from "../../../../helpers/verificadores";
import { formatarCPFCNPJ } from "../../../../helpers/masks";
import moment from "moment";
import BasicDatePicker from "../../../BasicDatePicker";
import { useEffect, useMemo } from "react";
import SelectNacionalidade from "./SelectNacionalidade";

export default function DadosBasicos({ spec, extras }: DynDadosCadastraisProps) {
    const { control, getValues, watch, setValue } = useFormContext();

    const possuiConjuge = watch(`${spec.name}.possui_conjuge`) === "Sim"

    const cpfOuCnpj = extras?.cpf_cnpj as string;

    const isCnpj = cpfOuCnpj ? cpfOuCnpj.replace(/\D/g, '').length > 11 : false;

    const opcoesRadio = useMemo(() => [
        { value: "Sim", label: "Sim" },
        { value: "Não", label: "Não" },
    ], [])

    const opcoesRegimeCasamento = useMemo(() => [
        { value: "Comunhão Parcial de Bens", label: "Comunhão Parcial de Bens" },
        { value: "Comunhão Universal de Bens", label: "Comunhão Universal de Bens" },
        { value: "Separação Total de Bens", label: "Separação Total de Bens" },
        { value: "Participação Final nos Aquestos", label: "Participação Final nos Aquestos" },
    ], [])

    const opcoesTipoRelacionamento = useMemo(() => [
        { value: "União estável sem comprovação", label: "União estável sem comprovação" },
        { value: "União estável com comprovação", label: "União estável com comprovação" },
        { value: "Casamento", label: "Casamento" },
    ], [])

    useEffect(() => {
        if (cpfOuCnpj) {
            setValue(`${spec.name}.documento`, formatarCPFCNPJ(cpfOuCnpj));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cpfOuCnpj]);

    return (
        <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Typography fontWeight={'bold'} variant="h6">Dados do Cooperado</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <Typography fontWeight={'bold'}>Dados Básicos</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name={`${spec.name}.nome_publico`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error, invalid } }) => {
                        return (
                            <TextField
                                {...field}
                                required
                                error={invalid}
                                label="Nome"
                                fullWidth
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                slotProps={{ htmlInput: { maxLength: 40 } }}
                                helperText={error ? error.message : ""}
                            />
                        );
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name={`${spec.name}.nome_social`}
                    control={control}
                    render={({ field, fieldState: { error, invalid } }) => {
                        return (
                            <TextField
                                {...field}
                                error={invalid}
                                label="Nome Social"
                                fullWidth
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                slotProps={{ htmlInput: { maxLength: 40 } }}
                                helperText={error ? error.message : ""}
                            />
                        );
                    }}
                />
            </Grid>
            {
                isCnpj &&
                <>
                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name={`${spec.name}.documento`}
                            control={control}
                            rules={{
                                required: "Campo obrigatório",
                                validate: (value) => {
                                    return validarCpfCnpj(value) || 'CNPJ inválido';
                                },
                            }}
                            render={({ field, fieldState: { error, invalid } }) => {
                                return (
                                    <TextField
                                        {...field}
                                        required
                                        error={invalid}
                                        defaultValue={formatarCPFCNPJ(cpfOuCnpj)}
                                        label="CNPJ"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                        slotProps={{ htmlInput: { maxLength: 17 } }}
                                        helperText={error ? error.message : ""}
                                    />
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name={`${spec.name}.data_nascimento`}
                            control={control}
                            rules={{
                                required: 'Campo obrigatório',
                                validate: (value) => {
                                    const dataMoment = moment(value, "YYYY-MM-DD", true);
                                    if (!dataMoment.isValid()) return "Data inválida";
                                    if (dataMoment.isAfter(moment())) return "Data não pode ser futura";
                                    return true;
                                }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <BasicDatePicker
                                    required
                                    label="Data de Fundação"
                                    helperText={error ? error.message : ""}
                                    error={invalid}
                                    value={field.value ? moment(field.value, "YYYY-MM-DD") : null}
                                    blockFutureDates
                                    futureDates={moment().subtract(1, 'days')}
                                    onChange={(data) => {
                                        field.onChange(data ? moment(data).format("YYYY-MM-DD") : null);
                                    }}
                                    formatString="DD/MM/YYYY"
                                    readonly={false}
                                />
                            )}
                        />
                    </Grid>
                </>
            }
            {
                !isCnpj &&
                <>
                    <Grid size={{ xs: 4 }}>
                        <Controller
                            name={`${spec.name}.rg`}
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    {...field}
                                    required
                                    label='RG'
                                    type="text"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    error={invalid}
                                    helperText={error ? error.message : ""}
                                    slotProps={{ htmlInput: { maxLength: 20 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Controller
                            name={`${spec.name}.documento`}
                            control={control}
                            rules={{
                                required: "Campo obrigatório",
                                validate: (value) => {
                                    return validarCpfCnpj(value) || 'CPF inválido';
                                },
                            }}
                            render={({ field, fieldState: { error, invalid } }) => {
                                return (
                                    <TextField
                                        {...field}
                                        required
                                        error={invalid}
                                        defaultValue={formatarCPFCNPJ(cpfOuCnpj)}
                                        label="CPF"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                        slotProps={{ htmlInput: { maxLength: 17 } }}
                                        helperText={error ? error.message : ""}
                                    />
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Controller
                            name={`${spec.name}.sexo`}
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field, fieldState: { error, invalid } }) => {
                                return (
                                    <FormControl fullWidth error={invalid} required>
                                        <InputLabel id="sexo-select">Sexo</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="sexo-select"
                                            value={field.value}
                                            label="Sexo"
                                        >
                                            <MenuItem value="M">Masculino</MenuItem>
                                            <MenuItem value="F">Feminino</MenuItem>
                                        </Select>
                                        <FormHelperText>{error ? error.message : ""}</FormHelperText>
                                    </FormControl>
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name={`${spec.name}.nome_mae`}
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field, fieldState: { error, invalid } }) => {
                                return (
                                    <TextField
                                        {...field}
                                        required
                                        error={invalid}
                                        label="Nome da Mãe"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        helperText={error ? error.message : ""}
                                    />
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name={`${spec.name}.nome_pai`}
                            control={control}
                            render={({ field }) => {
                                return (
                                    <TextField
                                        {...field}
                                        label="Nome do Pai"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                        <Controller
                            name={`${spec.name}.data_nascimento`}
                            control={control}
                            rules={{
                                required: 'Campo obrigatório',
                                validate: (value) => {
                                    const dataMoment = moment(value, "YYYY-MM-DD", true);
                                    if (!dataMoment.isValid()) return "Data inválida";
                                    if (dataMoment.isAfter(moment())) return "Data não pode ser futura";
                                    return true;
                                }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <BasicDatePicker
                                    required
                                    label="Data de Nascimento"
                                    helperText={error ? error.message : ""}
                                    error={invalid}
                                    value={field.value ? moment(field.value, "YYYY-MM-DD") : null}
                                    blockFutureDates
                                    futureDates={moment().subtract(1, 'days')}
                                    onChange={(data) => {
                                        field.onChange(data ? moment(data).format("YYYY-MM-DD") : null);
                                    }}
                                    formatString="DD/MM/YYYY"
                                    readonly={false}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Controller
                            name={`${spec.name}.nacionalidade`}
                            control={control}
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field, fieldState: { error, invalid } }) => {
                                return (
                                    <SelectNacionalidade
                                        required
                                        value={field.value}
                                        onChange={(value) => field.onChange(value)}
                                        error={invalid}
                                        errorMessage={error ? error.message : undefined}
                                    />
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                        <Controller
                            name={`${spec.name}.possui_conjuge`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl {...field} required error={invalid}>
                                    <FormLabel id="possui_conjuge">Possui cônjuge/companheiro?</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="Possui cônjuge/companheiro ?"
                                        value={getValues(`${spec.name}.possui_conjuge`)}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    >
                                        {
                                            opcoesRadio.map((opcao, index) => (
                                                <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                            ))
                                        }
                                    </RadioGroup>
                                    {invalid && <FormHelperText error>{error?.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                </>
            }
            {
                possuiConjuge &&
                <>
                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name={`${spec.name}.regime_casamento`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth {...field} error={fieldState.invalid} required>
                                    <InputLabel id="regime_casamento-select">Regime de Casamento</InputLabel>
                                    <Select
                                        labelId="regime_casamento-select"
                                        value={field.value}
                                        label="Regime de Casamento"
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {opcoesRegimeCasamento.map((op, index) => (
                                            <MenuItem key={index} value={op.value}>{op.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {fieldState.error && (
                                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name={`${spec.name}.tipo_relacionamento`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth {...field} error={fieldState.invalid} required>
                                    <InputLabel id="tipo_relacionamento-select">Tipo de Relacionamento</InputLabel>
                                    <Select
                                        labelId="tipo_relacionamento-select"
                                        value={field.value}
                                        label="Tipo de Relacionamento"
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {opcoesTipoRelacionamento.map((op, index) => (
                                            <MenuItem key={index} value={op.value}>{op.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {fieldState.error && (
                                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Grid>

                </>
            }
        </Grid>
    )
}
